import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useAppSelector } from "../../hooks"
import { selectUser } from "../../redux/slice/auth"
import { setNeedClearNewMessageCount } from "../../redux/slice/ordersList"
import {
  addAllMessage,
  addMessage,
  selectChat,
  setAllReadMessages,
  setActiveChat,
  updateReadMessages,
  setIDMessageToOpen,
} from "../../redux/slice/chat"
import { getApiUrl } from "../../utils/getApiUrl"
import uploadFile from "../../utils/upload/uploadFile"
import { v4 as uuidv4 } from "uuid"
import TitleBack from "../TitleBack/TitleBack"
import UploadBox from "../Assets/UploadBox/UploadBox"
import ReactTextareaAutosize from "react-textarea-autosize"
import AttachmentItemChat from "../Modals/ChatModal/assets/AttachmentItemChat"
import { SendPaperIcon } from "../../icons/SendPaperIcon"
import ScrollBlock from "../Assets/ScrollBlock"
import { Scrollbars } from "react-custom-scrollbars-2"
import clsx from "clsx"
import { videoFormats } from "../../utils/constants"
import ImagesGallery from "../Assets/ImagesGallery/ImagesGallery"
import { useMarkReadMessageMutation, useSendChatMessageMutation } from "../../redux/api/chat"
import { IChatMessage } from "../../types/chat"
import useWindowSize from "../../hooks/useWindowSize"
import ChatMessages from "./ChatMessages"
import ContextMenu, { contextMenuFcn } from "../Assets/ContextMenu/ContextMenu"
import ChatReply from "./ChatReply/ChatReply"
import styles from "../Modals/ChatModal/ChatModal.module.scss"
import { PusherPresenceChannel } from "laravel-echo/dist/channel"

interface Props {
  chatID: string
  orderInfo: any
  projectID?: string
  isFirstLoadMess: boolean
  setOpenChat?: any
  emptyTxt?: string
  // одиночный чат или модалка со всеми чатами
  isChatList?: boolean
  setUnreadMessList?: Dispatch<SetStateAction<string[]>>
  isViewChat?: boolean
  className?: string
}

const Chat: FC<Props> = ({
  chatID,
  isChatList,
  orderInfo,
  projectID,
  isFirstLoadMess,
  setOpenChat,
  emptyTxt,
  setUnreadMessList,
  isViewChat,
  className,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const dispatch = useDispatch()
  const { isDesktop } = useWindowSize()
  const [message, setMessage] = useState("")
  const [photoArr, setPhotoArr] = useState<any[]>([])
  const [attachmetsArr, setAttachmetsArr] = useState<any[]>([])
  const [focusedInput, setFocusedInput] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [messages, setMessages] = useState<IChatMessage[]>([])
  const [replyMessage, setReplyMessage] = useState<IChatMessage | null>(null)
  // Контекстное меню
  const [contextMenu, setContextMenu] = useState<boolean>(false)
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 })
  const [contextData, setContextData] = useState<any>(false)

  const nowUser = useAppSelector(selectUser)
  const { chats, allMessage, idMessageToOpen } = useAppSelector(selectChat)

  const gallery = useRef<any>(false)

  const [photoGallery, setPhotoGallery] = useState<any>([])
  const [galleryOpen, setGalleryOpen] = useState(false)

  const siteUrl = getApiUrl()?.replace("/api/v1", "") ?? window?.location?.origin

  const [sendChatMessage] = useSendChatMessageMutation()
  const [markReadedMessage] = useMarkReadMessageMutation()

  const scrollbarRef = useRef<Scrollbars>(null)
  const isFirstScroll = useRef<any>(false)
  const isFirstIdNewMess = useRef<any>(false)
  const idNewMess = useRef<any>(false)
  const typingTimer = useRef<NodeJS.Timer>()

  const updatePhotoArr = (data: any) => {
    setPhotoArr((prev) => [...prev.filter((f: any) => f.frontId !== data.frontId), data])
  }

  const updateAttachmetsArr = (data: any) => {
    setAttachmetsArr((prev) => [...prev.filter((f: any) => f.frontId !== data.frontId), data])
  }

  const removePhoto = (id: string) => {
    setPhotoArr((prev) => [...prev.filter((f: any) => f.id !== id)])
  }

  const removeFile = (id: string) => {
    setAttachmetsArr([...attachmetsArr.filter((f: any) => f.id !== id)])
  }

  const elementIsViewport = (element: string) => {
    const nowScrollbar = scrollbarRef?.current?.container

    const el = nowScrollbar?.querySelector(element)
    if (nowScrollbar && el) {
      const isViewTop = el?.getBoundingClientRect()?.top - nowScrollbar.getBoundingClientRect().top
      const isViewBottom = el?.getBoundingClientRect().top - nowScrollbar.getBoundingClientRect().top + el?.clientHeight
      return (
        (isViewTop > 0 && isViewTop < nowScrollbar.clientHeight) ||
        (isViewBottom > 0 && isViewBottom < nowScrollbar.clientHeight)
      )
    } else {
      return false
    }
  }

  useEffect(() => {
    setMessages(isChatList ? chats.find((chat) => chat.id === chatID)?.messages || [] : allMessage || [])
  }, [chatID, chats, isChatList, allMessage])

  useEffect(() => {
    setMessage("")
    setPhotoArr([])
    setAttachmetsArr([])
  }, [chatID])

  useEffect(() => {
    if (typeof window.Echo === "undefined" || !chatID) return
    const channel = window.Echo.join(`chats.${chatID}`)
    channel?.listen(".sendMassageFromChanel", (event: IChatMessage) => {
      if (isChatList) {
        dispatch(addMessage({ chatID: chatID, message: event }))
      } else {
        dispatch(addAllMessage(event))
      }
    })

    return () => {
      window.Echo.leave(`chats.${chatID}`)
      setReplyMessage(null)
      if (window.innerWidth < 1200) {
        dispatch(
          setActiveChat({
            id: "",
            name: null,
          }),
        )
      }
    }
  }, [chatID, isChatList])

  useEffect(() => {
    if (messages.length > 0 && scrollbarRef?.current) {
      const nowScrollbar = scrollbarRef?.current?.container
      if (
        isFirstLoadMess ||
        scrollbarRef?.current?.getScrollHeight() -
          scrollbarRef?.current?.getScrollTop() -
          nowScrollbar?.clientHeight ===
          0 ||
        elementIsViewport(".chatMessage:last-child") ||
        elementIsViewport(".chatMessage:nth-last-child(-n+2)")
      ) {
        const newM = nowScrollbar?.querySelector(".chatNewMess")
        if (isFirstLoadMess && isFirstScroll.current === false && newM) {
          scrollbarRef?.current?.scrollTop(
            newM.getBoundingClientRect()?.top - nowScrollbar?.getBoundingClientRect().top,
          )
          isFirstScroll.current = true
        } else {
          // scrollbarRef?.current?.scrollToBottom(isFirstLoadMess ? 350 : 0)
          scrollbarRef?.current?.scrollToBottom()
        }
      }
    }

    const removeIds: string[] = [...messages.filter((el) => !el.is_readed).map((el) => el.id)]

    const currentChat = chats.find((chat) => chat.id === messages[0]?.chat_dialog_id)
    if (removeIds.length > 0) {
      if (!isDesktop && !isViewChat) return
      setTimeout(() => {
        markReadedMessage({
          ids: removeIds,
          project_id: currentChat?.project_id || projectID || "",
        })
        // TODO dispatch(updateReadMessages(removeIds)) - заменить, в редаксе добавить аналог для установки прочитанных сообщений
        // Установка прочитанных сообщений
        dispatch(updateReadMessages(removeIds))
        if (setUnreadMessList) setUnreadMessList(removeIds)
        dispatch(setNeedClearNewMessageCount(String(orderInfo?.id)))
        if (messages?.length) dispatch(setAllReadMessages(messages[0].chat_dialog_id))
      }, 400)

      setTimeout(() => {
        idNewMess.current = false
      }, 3_000)
    }
  }, [messages, scrollbarRef, isViewChat, isDesktop])

  useEffect(() => {
    // скролл к сообщению в чате
    if (!idMessageToOpen || !messages?.length) return
    const nowScrollbar = scrollbarRef?.current?.container
    if (!nowScrollbar) return
    const messageBlock = nowScrollbar.querySelector(`[data-message-id='${idMessageToOpen}']`)
    if (!messageBlock) return
    const messageBlockTop = messageBlock.getBoundingClientRect().top
    if (!elementIsViewport(`[data-message-id='${idMessageToOpen}']`) || messageBlockTop < 150) {
      messageBlock.scrollIntoView()
    }
    dispatch(setIDMessageToOpen(""))
    messageBlock.classList.add("chatMessage--is-target")
    setTimeout(() => {
      messageBlock.classList.remove("chatMessage--is-target")
    }, 2_000)
  }, [idMessageToOpen, messages])

  const scrollToBottomChat = () => {
    if (
      scrollbarRef?.current &&
      scrollbarRef.current.getScrollHeight() -
        scrollbarRef.current.getScrollTop() -
        scrollbarRef.current.container?.clientHeight ===
        0
    ) {
      setTimeout(() => {
        scrollbarRef.current?.scrollToBottom()
      }, 350)
    }
  }

  const pastData = (event: any) => {
    const items = (event.clipboardData || event.originalEvent.clipboardData).items
    let blob: any = null

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") === 0) {
        blob = items[i].getAsFile()
      }
    }

    if (blob !== null) {
      const reader = new FileReader()
      const currentProjectID = projectID || chats.find((c) => c.id === chatID)?.project_id || ""
      reader.onload = function (event) {
        //  todo Добавить загрузчик файлов, урлы те же, что и на админке, только тут отсутсвует /web в урле
        uploadFile({
          file: reader.result,
          reader: blob,
          updatePhotoArr,
          updateAttachmetsArr,
          setIsLoading,
          scrollToBottomChat,
          siteUrl,
          currentProjectID,
        })
      }
      reader.readAsDataURL(blob)
    }
  }

  const pushSocketTyping = (id: string, typing: boolean) => {
    window.Echo?.connector?.pusher?.channel("presence-mainStaq")?.trigger("client-typing", {
      chatID: id,
      userID: nowUser?.id,
      userName: nowUser?.name,
      typing: typing,
    })
  }

  const handleTyping = (str: string) => {
    if (!chatID || typeof window.Echo === "undefined") return
    if (!str?.length) {
      pushSocketTyping(chatID, false)
      clearInterval(typingTimer.current)
      typingTimer.current = undefined
      return
    }
    if (!typingTimer.current) {
      pushSocketTyping(chatID, Boolean(str))
    } else {
      clearInterval(typingTimer.current)
    }
    typingTimer.current = setTimeout(() => {
      pushSocketTyping(chatID, Boolean(str))
      typingTimer.current = undefined
    }, 5_000)
  }

  const sendMessage = () => {
    if (message.trim().length > 0 || photoArr.length > 0 || attachmetsArr.length > 0) {
      const idMess = uuidv4()

      const messageData = {
        id: idMess,
        chat_dialog_id: chatID,
        user: {
          id: nowUser.id,
          name: nowUser.name,
          avatar_id: nowUser.avatar_id,
          addPhoneNumber: "",
          company_id: "",
          departmentName: "",
          email: "",
          externalId: "",
          folderName: "",
          folder_id: "",
          isAdmin: false,
          is_blocked: false,
          lastLogin: "",
          login: "",
          phoneNumber: "",
          position: "",
          roles: [],
          skills: [],
          work_schedule_id: "",
        },
        text: message,
        photos: [...photoArr.map((el: any) => el.id)],
        photos_info: [
          ...photoArr.map((el: any) => {
            return {
              createdAt: el.createdAt,
              filename: el.filename,
              size: el.size,
              id: el.id,
              previewId: el.id,
              width: el?.width,
              height: el?.height,
            }
          }),
        ],
        attachments: [...attachmetsArr.map((el: any) => el.id)],
        attachments_info: [
          ...attachmetsArr.map((el: any) => {
            return {
              id: el.id,
              filename: el.filename,
              size: el.size,
              createdAt: el.createdAt,
            }
          }),
        ],
        created_at: new Date().getTime(),
        is_readed: false,
        readed_at: null,
        reactions: [],
        readers: [],
        reply_id: replyMessage?.id || null,
        updated_at: null,
      }

      setMessage("")
      setAttachmetsArr([])
      setPhotoArr([])
      handleTyping("")

      const mes = {
        id: chatID,
        project_id: projectID || chats.find((c) => c.id === chatID)?.project_id || "",
        data: {
          id: idMess,
          text: message,
          attachments: [...attachmetsArr.map((el: any) => el.id)],
          photos: [...photoArr.map((el: any) => el.id)],
          reply_id: replyMessage?.id || null,
        },
      }

      if (isChatList) {
        dispatch(addMessage({ chatID: chatID, message: messageData }))
      } else {
        dispatch(addAllMessage(messageData))
      }

      // window.Echo?.connector?.pusher?.channel(`presence-chats.${chatID}`)?.trigger("client-addMessage", messageData)
      window.Echo?.connector?.pusher?.channel(`presence-chats.${chatID}`)
      setReplyMessage(null)
      sendChatMessage(mes)
    }
  }

  const stopVideo = () => {
    const allVideos = document.querySelectorAll(".image-gallery-slides video")
    if (allVideos.length) {
      allVideos.forEach((el: any) => el.pause())
    }
  }
  // апдейт фотогалереи
  const galleryUpdate = (photoInfo: any) => {
    const currentProject = projectID || chats.find((c) => c.id === chatID)?.project_id || ""
    setPhotoGallery([
      ...photoInfo.map((photoEl: any) => {
        const fileName = photoEl?.filename?.split(".")?.pop()?.toLowerCase() ?? ""
        return {
          original: `${getApiUrl() + "/public/photo/" + photoEl?.id + "?project_id=" + currentProject}`,
          thumbnail: `${getApiUrl() + "/public/photo/" + photoEl?.previewId + "?project_id=" + currentProject}`,
          id: photoEl?.id,
          isVideo: videoFormats.some((el) => el === fileName),
        }
      }),
    ])
  }

  // Открыть фото
  const openPhoto = (photoInfo: any, kk: number) => {
    galleryUpdate(photoInfo)
    setTimeout(() => {
      setGalleryOpen(true)
      gallery.current.slideToIndex(kk)
    }, 50)
  }

  // Открытие контекстного меню
  const contextMenuOpen = (e: any, el: string) => {
    contextMenuFcn(e, setContextMenu, setContextMenuPos)
    setContextData(el)
  }

  const handleReply = (data: IChatMessage) => {
    setReplyMessage(data)
    setContextMenu(false)
    setFocusedInput(true)
  }

  return (
    <>
      <div className={clsx(styles.chatModal, className)}>
        {setOpenChat && (
          <div className={styles.chatModal__head}>
            <TitleBack title={orderInfo?.name} onClick={() => setOpenChat(false)} noLinkBack />
          </div>
        )}
        <ScrollBlock className={`${styles.chatModal__main}`} hideTracksWhenNotNeeded ref={scrollbarRef}>
          <div className={`${styles.chatModal__mainListWp}`}>
            {!messages?.length && emptyTxt && <p className={styles.emptyTxt}>{emptyTxt}</p>}

            <ChatMessages
              messages={messages}
              idNewMess={idNewMess}
              isFirstIdNewMess={isFirstIdNewMess}
              openPhoto={openPhoto}
              contextMenuOpen={contextMenuOpen}
              projectID={projectID || chats.find((c) => c.id === chatID)?.project_id || ""}
            />
          </div>
        </ScrollBlock>
        <footer className={styles.chatModal__bottom}>
          {replyMessage?.id && <ChatReply setReplyMessage={setReplyMessage} message={replyMessage} />}
          <div className={styles.chatModal__inputWrap}>
            {/* <label className={styles.chatModal__append}>
              <img src="/img/clip.svg" alt="" />
              <FileDropzone name={"attachments"} setFieldValue={formik.setFieldValue} values={formik.values} />
          </label> */}
            <UploadBox
              updatePhotoArr={updatePhotoArr}
              updateAttachmetsArr={updateAttachmetsArr}
              setFocusedInput={setFocusedInput}
              scrollToBottomChat={scrollToBottomChat}
              setIsLoading={setIsLoading}
              projectID={projectID || chats.find((c) => c.id === chatID)?.project_id || ""}
            />
            <div className={styles.chatModal__inputWp}>
              <ReactTextareaAutosize
                minRows={1}
                maxRows={12}
                name={"chat-textarea"}
                placeholder={t("enterMessage")}
                value={message}
                className={styles.chatModal__input}
                onFocus={() => scrollToBottomChat()}
                onChange={(ev: any) => {
                  setMessage(ev.target.value)
                  handleTyping(ev.target.value)
                }}
                onKeyDown={(ev: any) => {
                  if (ev.keyCode === 13 && !ev.shiftKey) {
                    if (!isDesktop) return
                    ev.preventDefault()
                    sendMessage()
                  }
                }}
                onPaste={(ev: any) => pastData(ev)}
              />
              <div className={styles.chatUpload}>
                {photoArr.length > 0 ? (
                  <div className={styles.chatUpload__photos}>
                    {photoArr.map((el: any) => {
                      return (
                        <div className={styles.chatUpload__photo} key={el.id + Math.random() * 1000}>
                          <img
                            src={`${
                              el.preview
                                ? el.preview
                                : `${getApiUrl()}${process.env.NEXT_PUBLIC_API_SUFFIX}/photo/${el.id}`
                            }`}
                            alt={el.filename}
                          />
                          {el.progress && el.progress !== 100 && (
                            <div className={styles.chatUpload__photoProgress}>
                              {/* <CircleProgress progress={el.progress} /> */}
                              <span>{el.progress}%</span>
                            </div>
                          )}
                          <div
                            className={styles.chatUpload__removeImage}
                            onClick={() => {
                              removePhoto(el.id)
                            }}
                          >
                            <img src="/img/close.svg" alt="" />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
                {attachmetsArr.length > 0 ? (
                  <div className={styles.chatUpload__attachments}>
                    {attachmetsArr.map((el: any, kk) => {
                      return (
                        <AttachmentItemChat
                          key={el.id + Math.random() * 1000}
                          el={el}
                          iter={kk}
                          fileDelete={removeFile}
                          isAction={true}
                          isLoading={el.progress}
                        />
                      )
                    })}
                  </div>
                ) : null}
              </div>
            </div>
            <button
              className={styles.chatModal__submit}
              disabled={isLoading || undefined}
              onClick={() => sendMessage()}
            >
              <SendPaperIcon />
            </button>
          </div>
        </footer>
      </div>
      <div
        className={`${styles.chatMessage__gallery} ${
          galleryOpen ? styles.chatMessage__gallery_open : ""
        } defect-gallery`}
        onClick={(e: any) => {
          if (e.target?.classList?.contains("defect-gallery")) {
            setGalleryOpen(false)
          }
        }}
      >
        <ImagesGallery
          items={photoGallery}
          // @ts-ignore
          ref={gallery}
          onSlide={() => {
            stopVideo()
            const videoEl: any = document.querySelector(".image-gallery-slides .center video")
            if (videoEl) videoEl?.play?.()
          }}
          renderItem={(item: any) => {
            return item.isVideo ? (
              <video controls={true} className="image-gallery-image">
                <source src={item.original} />
              </video>
            ) : (
              <img className="image-gallery-image" alt="alt" src={item?.original} />
            )
          }}
        />
      </div>
      {contextMenu && (
        <ContextMenu
          setOpen={setContextMenu}
          contextMenuPos={contextMenuPos}
          onClose={() => {
            setContextData(false)
          }}
        >
          <button type="button" onClick={() => handleReply(contextData)}>
            {t("reply")}
          </button>
        </ContextMenu>
      )}
    </>
  )
}

export default Chat
